import { resolvedLineHeight, type TypeStyle } from "@/document/model/text";
import { measuredTextAdvances } from "@/renderer/text/canvasKitText";
import { ensureScriptFontReady } from "./scriptFonts";

export interface ScriptTextMeasureRequest {
  text: string;
  fontFamily?: string;
  fontSize?: number;
  fontWeight?: number;
  letterSpacing?: number;
  /** Line height in px. Omit / null for auto (fontSize × 1.25). */
  lineHeight?: number | null;
  /** When set, wraps like AUTO_HEIGHT and returns layout height. */
  maxWidth?: number | null;
}

export interface ScriptTextMeasureResult {
  width: number;
  height: number;
  baseline: number;
  /** Per Unicode code-point advance widths (including letterSpacing). */
  advances: number[];
}

let measureContext: CanvasRenderingContext2D | undefined;

/**
 * Sync text metrics for scripts. Uses the same Canvas2D path as the editor
 * fallback shaper. Call `load_font("Family")` first for non-document fonts.
 */
export function measureScriptText(request: ScriptTextMeasureRequest): ScriptTextMeasureResult {
  const text = request.text ?? "";
  const fontFamily = (request.fontFamily?.trim() || "Inter");
  const fontSize = finiteOr(request.fontSize, 12);
  const fontWeight = finiteOr(request.fontWeight, 400);
  const letterSpacing = finiteOr(request.letterSpacing, 0);
  const maxWidth = request.maxWidth != null && Number.isFinite(request.maxWidth) && request.maxWidth > 0
    ? request.maxWidth
    : Number.POSITIVE_INFINITY;

  ensureScriptFontReady(fontFamily);

  const style: TypeStyle = {
    fontFamily,
    fontWeight,
    fontSize,
    letterSpacing,
    lineHeight: request.lineHeight != null && Number.isFinite(request.lineHeight)
      ? { unit: "px", value: request.lineHeight }
      : "auto",
    horizontalAlign: "LEFT",
    verticalAlign: "TOP",
  };

  const advances = measureAdvances(text, style);
  const lineHeight = resolvedLineHeight(style);
  const baseline = fontSize * 0.82;

  if (!Number.isFinite(maxWidth) || text.length === 0) {
    const width = advances.reduce((sum, advance) => sum + advance, 0);
    return {
      width: Math.max(0, width),
      height: Math.max(lineHeight, text.length === 0 ? 0 : lineHeight),
      baseline,
      advances,
    };
  }

  // Word-wrap by code-point advances (same spirit as the editor fallback shaper).
  const characters = [...text];
  let lineWidth = 0;
  let maxLineWidth = 0;
  let lines = 1;
  for (let index = 0; index < characters.length; index += 1) {
    const ch = characters[index]!;
    const advance = advances[index] ?? 0;
    if (ch === "\n") {
      maxLineWidth = Math.max(maxLineWidth, lineWidth);
      lineWidth = 0;
      lines += 1;
      continue;
    }
    if (lineWidth > 0 && lineWidth + advance > maxWidth) {
      maxLineWidth = Math.max(maxLineWidth, lineWidth);
      lineWidth = advance;
      lines += 1;
      continue;
    }
    lineWidth += advance;
  }
  maxLineWidth = Math.max(maxLineWidth, lineWidth);

  return {
    width: Math.min(maxWidth, Math.max(0, maxLineWidth)),
    height: Math.max(lineHeight, lines * lineHeight),
    baseline,
    advances,
  };
}

/** Parse host JSON request → JSON result string for the Wasm bridge. */
export function measureScriptTextJson(requestJson: string): string {
  let request: ScriptTextMeasureRequest;
  try {
    request = JSON.parse(requestJson) as ScriptTextMeasureRequest;
  } catch {
    return JSON.stringify({ error: "measure_text request must be JSON" });
  }
  if (typeof request.text !== "string") {
    return JSON.stringify({ error: "measure_text requires a string `text` field" });
  }
  try {
    return JSON.stringify(measureScriptText(request));
  } catch (error) {
    const message = error instanceof Error ? error.message : "measure_text failed";
    return JSON.stringify({ error: message });
  }
}

function measureAdvances(text: string, style: TypeStyle): number[] {
  if (!text) {
    return [];
  }
  const context = tryGetMeasureContext();
  if (!context) {
    // Headless / test environments without Canvas2D — monospace-ish estimate.
    return [...text].map(ch => (ch === "\n" ? 0 : style.fontSize * 0.6 + style.letterSpacing));
  }
  context.font = `${style.fontWeight} ${style.fontSize}px "${style.fontFamily}", sans-serif`;
  return measuredTextAdvances(text, style.letterSpacing, value => context.measureText(value).width);
}

function tryGetMeasureContext(): CanvasRenderingContext2D | undefined {
  if (measureContext) {
    return measureContext;
  }
  if (typeof document === "undefined") {
    return undefined;
  }
  try {
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");
    if (!context) {
      return undefined;
    }
    measureContext = context;
    return context;
  } catch {
    return undefined;
  }
}

function finiteOr(value: number | undefined, fallback: number): number {
  return value != null && Number.isFinite(value) ? value : fallback;
}
